declare global {
    interface Window {
        nsWebViewInterface?: {
            emit(eventName: string, data: unknown): void
            on(eventName: string, callback: (data: unknown) => void): void
        }
    }
}

/**
 * True when this page is running inside the Siteline mobile app's embedded WebView (see
 * daily.client.mobile.ts / planscan.client.mobile.ts's onLoadFinished, which registers a native
 * WebViewInterface listener) rather than a real desktop/mobile browser. WKWebView doesn't reliably
 * support blob URLs, the anchor "download" attribute, or FileSaver's saveAs - the reason downloads
 * silently failed on iOS - so callers should branch on this and use sendFileToNativeShell instead
 * of their normal browser download call when true.
 *
 * @param isEmbedded the caller's `this.$store.state.isEmbedded` flag
 * @returns true if a native WebViewInterface bridge is available to receive files
 */
export function isRunningInAppWebView(isEmbedded: boolean): boolean {
    return isEmbedded && typeof window !== "undefined" && !!window.nsWebViewInterface
}

const ACK_TIMEOUT_MS = 2 * 60 * 1000

// sendUrlDownloadToNativeShell's downloads are a native fetch of a potentially large file (e.g. a
// daily attachments zip of full-res photos) over whatever connection the device currently has, so a
// slow/cellular download can legitimately take longer than ACK_TIMEOUT_MS - unlike
// sendFileToNativeShell's base64 path, whose work (decode + write + share) is local and fast
// regardless of network conditions. A longer timeout here avoids surfacing a false "failed to
// download" while native is still working and about to present the share sheet anyway.
const URL_DOWNLOAD_ACK_TIMEOUT_MS = 10 * 60 * 1000

interface AckPayload {
    requestId: number
    success: boolean
    error?: string
}

/**
 * Request/ack bookkeeping for a WebView->native bridge call that native acknowledges back on its own
 * "<event>Complete" event with {requestId, success, error?} - shared by sendFileToNativeShell and
 * sendUrlDownloadToNativeShell so each caller gets a promise that resolves/rejects on native's actual
 * completion (see registerNativeFileDownloadListener/registerNativeUrlDownloadListener in
 * common/nativeFileDownload.mobile.ts) instead of merely once the request was handed off - the two
 * downloads' bridge transports differ, but "wait for native's ack, with a timeout in case an older
 * app build never sends one" is identical either way.
 */
class NativeBridgeAckTracker {
    // Seeded with a random per-page-load epoch, not 0 - native's listeners outlive the page, so an
    // ack for a download started before an in-app reload could otherwise arrive after the new page
    // has registered its own tracker starting from the same id and resolve the wrong request.
    private nextRequestId = Math.floor(Math.random() * 1e9)
    private pending = new Map<number, { resolve: () => void; reject: (error: Error) => void }>()
    private isListenerRegistered = false

    public constructor(private readonly ackEventName: string, private readonly ackTimeoutMs: number = ACK_TIMEOUT_MS) {}

    private ensureListenerRegistered(): void {
        if (this.isListenerRegistered || !window.nsWebViewInterface) return
        this.isListenerRegistered = true

        window.nsWebViewInterface.on(this.ackEventName, (data: unknown) => {
            if (typeof data !== "object" || !data) return
            const { requestId, success, error } = data as Partial<AckPayload>
            if (typeof requestId !== "number" || !Number.isFinite(requestId) || typeof success !== "boolean") return
            const pending = this.pending.get(requestId)
            if (!pending) return

            this.pending.delete(requestId)
            if (success) pending.resolve()
            else pending.reject(new Error(error || "Request failed"))
        })
    }

    /**
     * Reserves a requestId for a new bridge call and returns it alongside the promise that resolves
     * once native's matching ack arrives (or rejects on timeout/failure) - the caller emits the
     * requestId as part of its own payload so native can echo it back.
     *
     * @returns the requestId to include in the emitted payload, and the promise to return to the caller
     */
    public register(): { requestId: number; promise: Promise<void> } {
        if (!window.nsWebViewInterface) return { requestId: -1, promise: Promise.reject(new Error("Native bridge unavailable")) }

        this.ensureListenerRegistered()

        const requestId = this.nextRequestId++
        const promise = new Promise<void>((resolve, reject) => {
            // Guards against an ack that never arrives - e.g. a native app build older than this fix,
            // shipped before native started emitting this completion event - so a caller driving a
            // loading indicator off this promise can't hang forever.
            const timeout = setTimeout(() => {
                if (!this.pending.delete(requestId)) return
                reject(new Error("Timed out waiting for native to confirm the request"))
            }, this.ackTimeoutMs)

            this.pending.set(requestId, {
                resolve: () => {
                    clearTimeout(timeout)
                    resolve()
                },
                reject: (error) => {
                    clearTimeout(timeout)
                    reject(error)
                }
            })
        })

        return { requestId, promise }
    }
}

const fileDownloadAcks = new NativeBridgeAckTracker("nativeFileDownloadComplete")
const urlDownloadAcks = new NativeBridgeAckTracker("nativeUrlDownloadComplete", URL_DOWNLOAD_ACK_TIMEOUT_MS)

/**
 * Hands a client-generated file to the native app shell to be saved and shared via the OS share
 * sheet - the mobile equivalent of triggering a browser download, routed over the same
 * nativescript-webview-interface bridge already used elsewhere (see emitToNativeShell in
 * worklog.client.base.ts, getBoxAuthUrl in protest.store.ts) for WebView-to-native messaging. The
 * native listener (registered alongside onLoadFinished) decodes the base64 payload, writes it to
 * the app sandbox, and presents the native share sheet.
 *
 * The returned promise resolves/rejects on native's "nativeFileDownloadComplete" ack (see
 * registerNativeFileDownloadListener), not merely once the request has been handed off - callers use
 * this to drive a loading indicator for the download's actual duration and to surface a real failure.
 *
 * @param content the file's raw content - a string for text formats (CSV/JSON), or a binary buffer for PDF/xlsx
 * @param filename the filename (with extension) to present to the user
 * @param mimeType the file's mime type
 * @returns a promise that resolves once native confirms the file was saved and shared, or rejects with why it failed
 */
export function sendFileToNativeShell(content: string | ArrayBuffer | Uint8Array, filename: string, mimeType: string): Promise<void> {
    let base64: string
    if (typeof content === "string") base64 = Buffer.from(content, "utf-8").toString("base64")
    else if (content instanceof Uint8Array) base64 = Buffer.from(content).toString("base64")
    else base64 = Buffer.from(new Uint8Array(content)).toString("base64")

    const { requestId, promise } = fileDownloadAcks.register()
    window.nsWebViewInterface?.emit("nativeFileDownload", { requestId, filename, base64, mimeType })

    return promise
}

/**
 * Hands a server-generated file's URL (rather than its bytes) to the native app shell, which fetches
 * and shares it itself - the mobile equivalent of sendFileToNativeShell, but for files that already
 * live behind a single authenticated GET endpoint instead of being composed client-side.
 *
 * sendFileToNativeShell round-trips the whole file through the WebView bridge as base64: the
 * WebView fetches it, then shuttles the bytes back out over nativescript-webview-interface's iOS
 * transport, which resolves a native evaluateJavaScript call's completion handler with the payload
 * string. That completion handler is unreliable for large strings - a multi-hundred-KB-to-multi-MB
 * base64 PDF (e.g. a Daily report with worklog attachment photos) comes back empty/nil instead of
 * erroring, so the "download" silently succeeds with a valid but 0-byte file. Small payloads (a CSV
 * export) fit fine, which is why this only shows up on larger PDFs/zips.
 *
 * Sending just the url/filename (a tiny payload, always within the bridge's limits) and letting
 * native fetch it directly - the same authenticated request downloadAndShareFile already makes for
 * the non-embedded native screens - avoids the bridge's return-value channel for the file bytes
 * entirely.
 *
 * The returned promise resolves/rejects on native's "nativeUrlDownloadComplete" ack (see
 * registerNativeUrlDownloadListener), not merely once the request has been handed off - callers use
 * this to drive a loading indicator for the download's actual duration and to surface a real failure,
 * instead of the UI going idle the instant the (fire-and-forget from the WebView's own perspective)
 * native fetch starts.
 *
 * @param url the authenticated API url to download the file from
 * @param filename the filename (with extension) to present to the user
 * @param shareCaption optional text shown alongside the file in the native share sheet
 * @returns a promise that resolves once native confirms the file was fetched and shared, or rejects with why it failed
 */
export function sendUrlDownloadToNativeShell(url: string, filename: string, shareCaption?: string): Promise<void> {
    const { requestId, promise } = urlDownloadAcks.register()
    window.nsWebViewInterface?.emit("nativeUrlDownload", { requestId, url, filename, shareCaption })

    return promise
}

/**
 * Same bridge as sendFileToNativeShell, for callers that already hold the file as a base64 string
 * (eg. a PDF received base64-encoded from the server). Passes it straight through instead of
 * decoding to bytes and immediately re-encoding back to base64.
 *
 * @param base64 the already base64-encoded file content
 * @param filename the filename (with extension) to present to the user
 * @param mimeType the file's mime type
 */
export function sendBase64FileToNativeShell(base64: string, filename: string, mimeType: string): void {
    window.nsWebViewInterface?.emit("nativeFileDownload", { filename, base64, mimeType })
}
